import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import '../styles/PromptsPage.css';
import { API_BASE_URL, getAuthHeaders, listAiProviders, listAiProviderModels, listVectorTools } from '../utils/api';
import { mergeSyntheticToolsIntoPickerList } from '../utils/syntheticChatTools';
import IngestionGroupMultiPicker from '../components/IngestionGroupMultiPicker';
import ApiQueryParamsEditor from '../components/ApiQueryParamsEditor';
import ToolCollisionConfirmModal from '../components/ToolCollisionConfirmModal';
import ModelCapabilityIcons from '../components/ModelCapabilityIcons';
import ToolSelector from '../components/ToolSelector';
import NameSlugFields from '../components/NameSlugFields';
import YumlConfigFields from '../components/YumlConfigFields';
import { slugifyName, getSlugConflictMessage, normalizeSlugInput } from '../utils/slugify';
import {
  parseIngestionGroupSlugs,
  fetchUnifiedKeysForGroup,
  intersectFieldLists,
  detectPromptTemplateCollisions,
  MAX_TOOL_INGESTION_GROUPS,
} from '../utils/toolGroupKeys';
import { useGroup } from '../contexts/GroupContext';
import { editorRowsFromOverridesObject, rowsToOverridesObject } from '../utils/apiQueryParams';
import { normalizeModelCapabilities } from '../utils/modelCapabilities';
import '../styles/ModelCapabilityIcons.css';

// Detect single-brace placeholders in text, honoring double-brace escaping.
// - Trims whitespace inside braces: { raw.doc } == {raw.doc}
// - Valid path pattern: [A-Za-z_][A-Za-z0-9_]*(.[A-Za-z_][A-Za-z0-9_]*)*
// - Returns unique variables and a list of invalid placeholders
function formatPromptIngestionGroups(prompt, groups) {
  const slugs = parseIngestionGroupSlugs(prompt);
  if (!slugs.length) return 'None';
  return slugs
    .map((slug) => {
      const g = (groups || []).find((x) => (x.group_slug || x.id) === slug);
      return g ? (g.name || g.group_slug) : slug;
    })
    .join(', ');
}

function detectPlaceholders(input) {
  const text = typeof input === 'string' ? input : '';
  if (!text) return { variables: [], invalids: [] };
  const OPEN = '\uFFF0';
  const CLOSE = '\uFFF1';
  // Mask escaped braces so they are treated as literals during detection
  const masked = text.replace(/\{\{/g, OPEN).replace(/\}\}/g, CLOSE);
  const variables = [];
  const invalids = [];
  const re = /\{([^{}]*)\}/g;
  let m;
  while ((m = re.exec(masked)) !== null) {
    const inside = (m[1] || '').trim();
    if (!inside) {
      invalids.push({ raw: '{}', reason: 'Empty placeholder' });
      continue;
    }
    const ok = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*$/.test(inside);
    if (ok) {
      if (!variables.includes(inside)) variables.push(inside);
    } else {
      invalids.push({ raw: `{ ${inside} }`.replace(/\s+/g, ' ').trim(), reason: 'Invalid path syntax' });
    }
  }
  return { variables, invalids };
}

const MODEL_FALLBACK_ID = 'gpt-4o-mini';

function formatStoredPromptModel(model) {
  const id = String(model || '').trim();
  return id || 'Your active model (Settings → User)';
}

/** Normalize prompt.tools (array of ids or object map) to a string[] of tool ids. */
function parsePromptToolIds(prompt) {
  const toolsBlob = prompt?.tools;
  if (Array.isArray(toolsBlob)) {
    return toolsBlob.map((x) => String(x || '').trim()).filter(Boolean);
  }
  if (toolsBlob && typeof toolsBlob === 'object') {
    return Object.keys(toolsBlob).map((k) => String(k || '').trim()).filter(Boolean);
  }
  return [];
}

function inferProviderSlugFromModelId(modelId) {
  const id = String(modelId || '').trim().toLowerCase();
  if (!id) return '';
  if (id.startsWith('gpt-') || /^o\d/.test(id)) return 'openai';
  if (id.startsWith('claude')) return 'anthropic';
  if (id.startsWith('gemini')) return 'google';
  if (id.startsWith('llama') || id.startsWith('mistral') || id.startsWith('qwen')) return 'ollama';
  return '';
}

function normalizeProviderRecord(provider) {
  if (!provider || typeof provider !== 'object') return null;
  const id = provider.id || null;
  const slug = String(provider.slug || '').trim().toLowerCase();
  const name = String(provider.name || provider.slug || provider.api_type || 'Provider').trim();
  const apiType = String(provider.api_type || provider.apiType || '').trim().toLowerCase();
  if (!id && !slug) return null;
  return {
    id,
    slug,
    name,
    apiType,
    enabled: true
  };
}

function normalizeProviderModelRecord(modelRow) {
  if (!modelRow || typeof modelRow !== 'object') return null;
  const provider = normalizeProviderRecord(modelRow.ai_providers || modelRow.provider || null);
  const modelId = String(modelRow.model_id || modelRow.modelId || modelRow.id || '').trim();
  if (!modelId) return null;
  return {
    id: modelId,
    name: String(modelRow.display_name || modelRow.displayName || modelId).trim(),
    providerId: provider?.id || modelRow.provider_id || modelRow.providerId || null,
    providerSlug: provider?.slug || '',
    providerName: provider?.name || '',
    providerApiType: provider?.apiType || '',
    capabilities: modelRow.capabilities || modelRow?.catalog?.capabilities || null,
    isEmbedding: modelRow.is_embedding === true || modelRow.isEmbedding === true || modelRow.model_type === 'embedding',
    modelType: modelRow.model_type || modelRow.modelType || (modelRow.is_embedding === true ? 'embedding' : 'llm'),
    enabled: true,
    source: 'registry'
  };
}

function PromptsPage() {
  const { groups, currentGroup } = useGroup();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [modalType, setModalType] = useState(null); // 'main', 'review', 'management', or null for edit
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeModelLabel, setActiveModelLabel] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const promptsLoadedRef = useRef(false);
  const modelsLoadedRef = useRef(false);
  const promptsLoadingRef = useRef(false);
  const modelsLoadingRef = useRef(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    prompt: '',
    system_message: '',
    response_format: '',
    temperature: '1',
    model: MODEL_FALLBACK_ID,
    provider_id: '',
    provider_slug: '',
    category: 'general',
    page_location: null,
    ingestion_group_slug: '',
    ingestion_group_slugs: [],
    tools: [],
    // New: structured outputs builder state
    response_outputs: [],
    // New: can the agent review approve/disapprove entries
    response_can_review: false,
    yuml_config: { enabled: false, template_id: null, summary_keys: [], emit_on: 'after_output' },
    api_query_override_rows: [],
  });

  // Left sidebar state for the Prompt editor modal
  const [leftTab, setLeftTab] = useState('inputs'); // 'inputs' | 'help'
  const [rawKeys, setRawKeys] = useState([]); // from data input settings (columns)
  const [rawKeysLoading, setRawKeysLoading] = useState(false);
  const [rawKeysError, setRawKeysError] = useState('');
  // Output keys grouped per ingestion group
  const [outputKeys, setOutputKeys] = useState([]); // kept for backward-compat if needed (combined list)
  // Mark as used for eslint even when conditionally empty in some flows
  // eslint-disable-next-line no-unused-expressions
  void outputKeys;
  const [outputDefinedKeys, setOutputDefinedKeys] = useState([]);
  const [outputExistingKeys, setOutputExistingKeys] = useState([]);
  const [outputKeysLoading, setOutputKeysLoading] = useState(false);
  const [outputKeysError, setOutputKeysError] = useState('');
  const outputKeysLoadedRef = useRef(false);
  const rawKeysLoadedRef = useRef(false);
  const [copiedToken, setCopiedToken] = useState('');
  const [collisionModalOpen, setCollisionModalOpen] = useState(false);
  const [pendingCollisions, setPendingCollisions] = useState({ collisions: [], pairs: [] });
  const [pendingPromptPayload, setPendingPromptPayload] = useState(null);
  const [idTouched, setIdTouched] = useState(false);

  const selectedGroupSlugs = useMemo(() => {
    const fromForm = Array.isArray(formData.ingestion_group_slugs) ? formData.ingestion_group_slugs : [];
    if (fromForm.length) return fromForm.map((s) => String(s).trim()).filter(Boolean);
    const legacy = String(formData.ingestion_group_slug || '').trim();
    return legacy ? [legacy] : [];
  }, [formData.ingestion_group_slugs, formData.ingestion_group_slug]);

  const existingPromptIds = useMemo(
    () => prompts.map((p) => p.id).filter(Boolean),
    [prompts]
  );

  const idConflict = useMemo(() => {
    if (editingPrompt) return null;
    return getSlugConflictMessage(formData.id, formData.name, existingPromptIds);
  }, [editingPrompt, formData.id, formData.name, existingPromptIds]);

  // Files discovered for this group (virtual keys)
  const [fileKeysFile, setFileKeysFile] = useState([]); // tokens like file.<field> that attach files
  const [fileKeysText, setFileKeysText] = useState([]); // tokens like file.<field> that inline text
  const [fileKeysError, setFileKeysError] = useState('');

  // Active suffix for ingestion-related APIs
  const [activeSuffix, setActiveSuffix] = useState('');

  // Temp input state for adding new output keys and validation
  const [newOutputKey, setNewOutputKey] = useState('');
  const [outputError, setOutputError] = useState('');
  const [, setNewOutputDesc] = useState('');
  // Inline row errors for outputs (by index)
  const [outputRowErrors, setOutputRowErrors] = useState({});
  // Which output row is currently in edit mode (null = none)
  const [editingOutputIndex, setEditingOutputIndex] = useState(null);
  // Draft entry while editing so underlying data isn't mutated until confirm
  const [editingDraftEntry, setEditingDraftEntry] = useState(null);

  // Local state for searchable model dropdown
  const [modelQuery, setModelQuery] = useState('');
  const [showModelList, setShowModelList] = useState(false);
  const [modelHighlight, setModelHighlight] = useState(0);
  const modelInputRef = useRef(null);
  const modelListRef = useRef(null);

  const [availableTools, setAvailableTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);

  const isWorkflowButtonForm = useMemo(() => {
    if (modalType) return true;
    const loc = formData.page_location;
    return loc === 'main' || loc === 'review' || loc === 'management' || loc === 'project_chat_catalog';
  }, [modalType, formData.page_location]);

  const isProjectCatalogForm = useMemo(() => (
    modalType === 'project' || formData.page_location === 'project_chat_catalog'
  ), [modalType, formData.page_location]);

  // Load horizon tools for the prompt editor tool picker
  useEffect(() => {
    if (!showModal) return undefined;
    let cancelled = false;
    (async () => {
      setToolsLoading(true);
      try {
        const pageSize = 200;
        const first = await listVectorTools(1, pageSize);
        if (cancelled) return;
        let items = Array.isArray(first?.items) ? [...first.items] : [];
        const totalPages = Number(first?.totalPages) || 1;
        for (let page = 2; page <= totalPages; page += 1) {
          const next = await listVectorTools(page, pageSize);
          if (cancelled) return;
          if (Array.isArray(next?.items)) items = items.concat(next.items);
        }
        const byId = new Map();
        items.forEach((t) => {
          const id = String(t?.id || t?.metadata?.id || '').trim();
          if (!id) return;
          byId.set(id, {
            id,
            name: t.name || t.slug || id,
            slug: t.slug || '',
            description: t.description || '',
          });
        });
        setAvailableTools(mergeSyntheticToolsIntoPickerList(Array.from(byId.values())));
      } catch (e) {
        if (!cancelled) {
          console.error('[PromptsPage] Failed to load tools for selector', e);
          setAvailableTools([]);
        }
      } finally {
        if (!cancelled) setToolsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showModal, activeSuffix]);

  const providerScopedModels = useMemo(() => {
    if (!Array.isArray(models) || models.length === 0) return [];
    const llmOnly = models.filter((m) => normalizeModelCapabilities(m).modelType !== 'embedding');
    if (formData.provider_id) {
      return llmOnly.filter((m) => String(m.providerId || '') === String(formData.provider_id));
    }
    if (formData.provider_slug) {
      const slug = String(formData.provider_slug || '').trim().toLowerCase();
      return llmOnly.filter((m) => String(m.providerSlug || '').trim().toLowerCase() === slug);
    }
    return llmOnly;
  }, [models, formData.provider_id, formData.provider_slug]);

  // Derived filtered list for model combobox
  const filteredModels = useMemo(() => {
    const q = modelQuery.trim().toLowerCase();
    if (!q) return providerScopedModels;
    return providerScopedModels.filter(m => (m.name || m.id).toLowerCase().includes(q));
  }, [providerScopedModels, modelQuery]);

  const selectedModel = useMemo(() => (
    providerScopedModels.find((m) => m.id === formData.model)
    || models.find((m) => m.id === formData.model)
    || null
  ), [providerScopedModels, models, formData.model]);

  const selectedModelCapabilities = useMemo(() => normalizeModelCapabilities(selectedModel || {}), [selectedModel]);
  const hasFileFieldPlaceholders = (fileKeysFile || []).length > 0;
  const showPdfCapabilityWarning = hasFileFieldPlaceholders && selectedModel && selectedModelCapabilities.acceptsPdf !== true;

  const promptEditorModelDisplay = useMemo(() => {
    if (isWorkflowButtonForm) {
      return activeModelLabel
        ? `Active model: ${activeModelLabel}`
        : 'Active model from Settings → User';
    }
    const m = selectedModel || (Array.isArray(models) ? models.find((x) => x.id === formData.model) : null);
    if (m) {
      const provider = m.providerName || m.provider;
      return provider ? `${provider}: ${m.name || m.id}` : (m.name || m.id);
    }
    return formatStoredPromptModel(formData.model);
  }, [isWorkflowButtonForm, activeModelLabel, selectedModel, models, formData.model]);

  // Close model dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      const inputEl = modelInputRef.current;
      const listEl = modelListRef.current;
      if (!inputEl || !listEl) return;
      if (!inputEl.contains(e.target) && !listEl.contains(e.target)) {
        setShowModelList(false);
      }
    };
    if (showModelList) {
      document.addEventListener('mousedown', onDocClick);
    }
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showModelList]);

  useEffect(() => {
    // Prevent duplicate API calls (e.g., from React StrictMode or rapid tab switching)
    if (!promptsLoadedRef.current && !promptsLoadingRef.current) {
      promptsLoadedRef.current = true;
      promptsLoadingRef.current = true;
      loadPrompts().finally(() => {
        promptsLoadingRef.current = false;
      });
    }

    if (!modelsLoadedRef.current && !modelsLoadingRef.current) {
      modelsLoadedRef.current = true;
      modelsLoadingRef.current = true;
      loadModels().finally(() => {
        modelsLoadingRef.current = false;
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showModal || !Array.isArray(models) || models.length === 0) return;

    setFormData((prev) => {
      const currentModelId = String(prev.model || MODEL_FALLBACK_ID).trim() || MODEL_FALLBACK_ID;
      const byId = models.find((m) => m.id === currentModelId);
      const byHeuristic = !byId
        ? models.find((m) => String(m.providerSlug || '').trim().toLowerCase() === inferProviderSlugFromModelId(currentModelId))
        : null;
      const resolvedModel = byId || byHeuristic || null;

      let nextProviderId = prev.provider_id || '';
      let nextProviderSlug = prev.provider_slug || '';
      let nextModelId = currentModelId;

      if (!nextProviderId && !nextProviderSlug && resolvedModel) {
        nextProviderId = resolvedModel.providerId || '';
        nextProviderSlug = resolvedModel.providerSlug || '';
      }

      if (nextProviderId || nextProviderSlug) {
        const inProvider = models.filter((m) => {
          if (nextProviderId) return String(m.providerId || '') === String(nextProviderId);
          return String(m.providerSlug || '').trim().toLowerCase() === String(nextProviderSlug || '').trim().toLowerCase();
        });
        if (inProvider.length > 0 && !inProvider.some((m) => m.id === nextModelId)) {
          nextModelId = inProvider[0].id;
        }
      }

      if (
        nextProviderId === prev.provider_id &&
        nextProviderSlug === prev.provider_slug &&
        nextModelId === prev.model
      ) {
        return prev;
      }

      return {
        ...prev,
        provider_id: nextProviderId,
        provider_slug: nextProviderSlug,
        model: nextModelId
      };
    });
  }, [showModal, models]);

  useEffect(() => {
    if (!showModal) return;
    if (modelQuery) return;
    const selected = models.find((m) => m.id === formData.model);
    if (selected?.name) {
      setModelQuery(selected.name);
    }
  }, [showModal, modelQuery, models, formData.model]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ action: 'get' }),
        });
        const json = await resp.json().catch(() => ({}));
        if (cancelled || !resp.ok || !json?.success) return;
        const modelId = String(json.settings?.activeModelId || '').trim();
        setActiveModelLabel(modelId || MODEL_FALLBACK_ID);
      } catch (_) {
        if (!cancelled) setActiveModelLabel(MODEL_FALLBACK_ID);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadLegacyModels = useCallback(async () => {
    const modelsHeaders = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/casc/v1/api/models`, {
      method: 'POST',
      headers: modelsHeaders,
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      throw new Error(`Legacy models endpoint failed (${response.status})`);
    }

    const data = await response.json().catch(() => ({}));
    if (!data.success || !data.models) {
      throw new Error('Legacy models payload was empty');
    }

    const allModels = [];
    if (Array.isArray(data.models)) {
      allModels.push(...data.models);
    } else {
      Object.values(data.models).forEach((platformModels) => {
        if (Array.isArray(platformModels)) {
          allModels.push(...platformModels);
        }
      });
    }

    const normalized = allModels
      .map((m) => {
        const modelId = String(m?.id || '').trim();
        if (!modelId) return null;
        const providerSlug = inferProviderSlugFromModelId(modelId);
        return {
          id: modelId,
          name: String(m?.name || modelId).trim(),
          providerId: null,
          providerSlug,
          providerName: providerSlug ? providerSlug.charAt(0).toUpperCase() + providerSlug.slice(1) : '',
          providerApiType: providerSlug,
          capabilities: { acceptsText: true, canToolCall: true },
          isEmbedding: false,
          modelType: 'llm',
          enabled: true,
          source: 'legacy'
        };
      })
      .filter(Boolean);

    const providerMap = new Map();
    normalized.forEach((model) => {
      const slug = String(model.providerSlug || '').trim().toLowerCase();
      if (!slug || providerMap.has(slug)) return;
      providerMap.set(slug, {
        id: '',
        slug,
        name: model.providerName || slug,
        apiType: model.providerApiType || '',
        enabled: true
      });
    });

    return {
      providers: Array.from(providerMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
      models: normalized
    };
  }, []);

  const loadModels = useCallback(async () => {
    try {
      console.log('[PromptsPage] Loading providers/models from registry...');
      const [providersResult, providerModelsResult] = await Promise.all([
        listAiProviders(),
        listAiProviderModels()
      ]);

      const enabledProviders = (providersResult?.providers || [])
        .map(normalizeProviderRecord)
        .filter((p) => p && p.enabled)
        .sort((a, b) => a.name.localeCompare(b.name));
      const providerById = new Map(enabledProviders.map((p) => [String(p.id || ''), p]));

      const normalizedModels = (providerModelsResult?.models || [])
        .map((row) => {
          const normalized = normalizeProviderModelRecord(row);
          if (!normalized || !normalized.enabled) return null;
          if (normalized.providerId && !providerById.has(String(normalized.providerId))) return null;
          return normalized;
        })
        .filter(Boolean)
        .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));

      if (enabledProviders.length > 0 && normalizedModels.length > 0) {
        setProviders(enabledProviders);
        setModels(normalizedModels);
        console.log('[PromptsPage] Loaded provider/model registry:', {
          providers: enabledProviders.length,
          models: normalizedModels.length
        });
        return;
      }

      console.warn('[PromptsPage] Registry returned no enabled provider-model entries; falling back to legacy models endpoint');
      const fallback = await loadLegacyModels();
      setProviders(fallback.providers);
      setModels(fallback.models);
    } catch (error) {
      console.error('[PromptsPage] Error loading provider/model registry; using legacy fallback:', error);
      modelsLoadedRef.current = false; // Reset on error so it can retry
      try {
        const fallback = await loadLegacyModels();
        setProviders(fallback.providers);
        setModels(fallback.models);
        return;
      } catch (fallbackError) {
        console.error('[PromptsPage] Legacy model fallback failed:', fallbackError);
      }

      const hardcoded = [
        { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
        { id: 'gpt-4o', name: 'GPT-4o' },
        { id: 'gpt-4-turbo', name: 'GPT-4 Turbo' },
        { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
        { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet' },
        { id: 'claude-3-opus-20240229', name: 'Claude 3 Opus' },
        { id: 'gemini-pro', name: 'Gemini Pro' },
        { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro' }
      ];
      setProviders([
        { id: '', slug: 'openai', name: 'OpenAI', apiType: 'openai', enabled: true },
        { id: '', slug: 'anthropic', name: 'Anthropic', apiType: 'anthropic', enabled: true },
        { id: '', slug: 'google', name: 'Google', apiType: 'google', enabled: true }
      ]);
      setModels(hardcoded.map((m) => ({
        ...m,
        providerId: null,
        providerSlug: inferProviderSlugFromModelId(m.id),
        providerName: '',
        providerApiType: '',
        capabilities: { acceptsText: true, canToolCall: true },
        isEmbedding: false,
        modelType: 'llm',
        enabled: true,
        source: 'hardcoded'
      })));
    }
  }, [loadLegacyModels]);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/prompts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.prompts) {
          setPrompts(data.prompts);
        }
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMainPrompt = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      prompt: '',
      system_message: '',
      response_format: '',
      temperature: '1',
      model: MODEL_FALLBACK_ID,
      provider_id: '',
      provider_slug: '',
      category: 'main',
      page_location: 'main',
      ingestion_group_slug: currentGroup || '',
      ingestion_group_slugs: currentGroup ? [currentGroup] : [],
      tools: [],
      response_outputs: [],
      response_can_review: false,
      api_query_override_rows: [],
    });
    setNewOutputKey('');
    setNewOutputDesc('');
    setOutputError('');
    setOutputRowErrors({});
    setModelQuery('');
    setEditingPrompt(null);
    setModalType('main');
    setShowModal(true);
    setEditingOutputIndex(null);
    setEditingDraftEntry(null);
    // Initialize left panel
    setLeftTab('inputs');
    setCopiedToken('');
    outputKeysLoadedRef.current = false;
    rawKeysLoadedRef.current = false;
    setOutputKeys([]);
    setRawKeys([]);
  };

  const handleAddReviewPrompt = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      prompt: '',
      system_message: '',
      response_format: '',
      temperature: '1',
      model: MODEL_FALLBACK_ID,
      provider_id: '',
      provider_slug: '',
      category: 'review',
      page_location: 'review',
      ingestion_group_slug: currentGroup || '',
      ingestion_group_slugs: currentGroup ? [currentGroup] : [],
      tools: [],
      response_outputs: [],
      response_can_review: false,
      api_query_override_rows: [],
    });
    setNewOutputKey('');
    setNewOutputDesc('');
    setOutputError('');
    setOutputRowErrors({});
    setModelQuery('');
    setEditingPrompt(null);
    setModalType('review');
    setShowModal(true);
    setEditingOutputIndex(null);
    setEditingDraftEntry(null);
    setLeftTab('inputs');
    setCopiedToken('');
    outputKeysLoadedRef.current = false;
    rawKeysLoadedRef.current = false;
    setOutputKeys([]);
    setRawKeys([]);
  };

  const handleAddManagementPrompt = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      prompt: '',
      system_message: '',
      response_format: '',
      temperature: '1',
      model: MODEL_FALLBACK_ID,
      provider_id: '',
      provider_slug: '',
      category: 'management',
      page_location: 'management',
      ingestion_group_slug: currentGroup || '',
      ingestion_group_slugs: currentGroup ? [currentGroup] : [],
      tools: [],
      response_outputs: [],
      response_can_review: false,
      api_query_override_rows: [],
    });
    setNewOutputKey('');
    setNewOutputDesc('');
    setOutputError('');
    setOutputRowErrors({});
    setModelQuery('');
    setEditingPrompt(null);
    setModalType('management');
    setShowModal(true);
    setEditingOutputIndex(null);
    setEditingDraftEntry(null);
    setLeftTab('inputs');
    setCopiedToken('');
    outputKeysLoadedRef.current = false;
    rawKeysLoadedRef.current = false;
    setOutputKeys([]);
    setRawKeys([]);
  };

  const handleAddProjectPrompt = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      prompt: '',
      system_message: '',
      response_format: '',
      temperature: '1',
      model: MODEL_FALLBACK_ID,
      provider_id: '',
      provider_slug: '',
      category: 'project_workflow',
      page_location: 'project_chat_catalog',
      ingestion_group_slug: '',
      ingestion_group_slugs: [],
      tools: [],
      response_outputs: [],
      response_can_review: false,
      api_query_override_rows: [],
    });
    setNewOutputKey('');
    setNewOutputDesc('');
    setOutputError('');
    setOutputRowErrors({});
    setModelQuery('');
    setEditingPrompt(null);
    setModalType('project');
    setShowModal(true);
    setEditingOutputIndex(null);
    setEditingDraftEntry(null);
    setLeftTab('inputs');
    setCopiedToken('');
    outputKeysLoadedRef.current = false;
    rawKeysLoadedRef.current = false;
    setOutputKeys([]);
    setRawKeys([]);
  };

  const handleEditPrompt = (prompt) => {
    // Map any existing response_format to outputs list if present
    let outputs = [];
    let canReview = false;
    try {
      const rf = prompt.response_format;
      if (rf && rf.output && typeof rf.output === 'object' && !Array.isArray(rf.output)) {
        outputs = Object.entries(rf.output).map(([k, v]) => ({ key: k, description: typeof v === 'string' ? v : '' }));
      } else if (rf && Array.isArray(rf.outputs)) {
        // Backward compatibility with previous array-of-keys format
        outputs = rf.outputs.filter(k => typeof k === 'string').map(k => ({ key: k, description: '' }));
      }
      // read can_review regardless of whether output exists
      if (rf && typeof rf.can_review !== 'undefined') {
        canReview = !!rf.can_review;
      }
    } catch (_) { /* ignore */ }
    setFormData({
      id: prompt.id,
      name: prompt.name || '',
      description: prompt.description || '',
      prompt: prompt.prompt || '',
      system_message: prompt.system_message || '',
      response_format: prompt.response_format ? JSON.stringify(prompt.response_format, null, 2) : '',
      temperature: prompt.temperature?.toString() || '1',
      model: prompt.model || MODEL_FALLBACK_ID,
      provider_id: prompt?.runtime?.provider?.id || '',
      provider_slug: prompt?.runtime?.provider?.slug || inferProviderSlugFromModelId(prompt.model || ''),
      category: prompt.category || 'general',
      page_location: prompt.page_location || null,
      ingestion_group_slug: parseIngestionGroupSlugs(prompt)[0] || prompt.ingestion_group_slug || '',
      ingestion_group_slugs: parseIngestionGroupSlugs(prompt),
      tools: parsePromptToolIds(prompt),
      response_outputs: outputs,
      response_can_review: canReview,
      yuml_config: prompt.yuml_config && typeof prompt.yuml_config === 'object'
        ? prompt.yuml_config
        : { enabled: false, template_id: null, summary_keys: [], emit_on: 'after_output' },
      api_query_override_rows: editorRowsFromOverridesObject(prompt.metadata?.api_query_overrides || {}),
    });
    setNewOutputKey('');
    setNewOutputDesc('');
    setOutputError('');
    setOutputRowErrors({});
    setModelQuery('');
    setEditingPrompt(prompt);
    setModalType(null);
    setShowModal(true);
    setEditingOutputIndex(null);
    setEditingDraftEntry(null);
    setLeftTab('inputs');
    setCopiedToken('');
    outputKeysLoadedRef.current = false;
    rawKeysLoadedRef.current = false;
    setOutputKeys([]);
    setRawKeys([]);
  };

  const handleDeleteClick = (promptId) => {
    setPromptToDelete(promptId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!promptToDelete) {
      return;
    }

    setShowDeleteConfirm(false);
    const promptId = promptToDelete;
    setPromptToDelete(null);

    try {
      console.log(`[PromptsPage] Deleting prompt:`, promptId);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/prompts/delete/${encodeURIComponent(promptId)}`, {
        method: 'POST',
        headers,
      });

      console.log(`[PromptsPage] Delete response status:`, response.status);
      const data = await response.json();
      console.log(`[PromptsPage] Delete response data:`, data);

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Prompt deleted successfully' });
        setTimeout(() => {
          setMessage({ type: '', text: '' });
          loadPrompts();
        }, 1500);
      } else {
        const errorMsg = data.error || `Failed to delete prompt. Server returned status ${response.status}`;
        console.error(`[PromptsPage] Error deleting prompt:`, errorMsg);
        setMessage({ type: 'error', text: errorMsg });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('[PromptsPage] Exception deleting prompt:', error);
      setMessage({ type: 'error', text: `Network error: ${error.message || 'Failed to connect to server'}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setPromptToDelete(null);
  };

  const handleSavePrompt = async () => {
    try {
      if (idConflict) {
        setMessage({ type: 'error', text: idConflict });
        setTimeout(() => setMessage({ type: '', text: '' }), 6000);
        return;
      }
      // Validate required fields
      if (!formData.id || !formData.name || !formData.category) {
        setMessage({ type: 'error', text: 'Please fill in all required fields (ID, Name, Category)' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        return;
      }
      if (!isProjectCatalogForm && selectedGroupSlugs.length === 0) {
        setMessage({ type: 'error', text: 'Please select at least one ingestion group for this prompt.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 6000);
        return;
      }
      if (selectedGroupSlugs.length > MAX_TOOL_INGESTION_GROUPS) {
        setMessage({ type: 'error', text: `Maximum ${MAX_TOOL_INGESTION_GROUPS} ingestion groups per prompt.` });
        setTimeout(() => setMessage({ type: '', text: '' }), 6000);
        return;
      }

      // Validate outputs to ensure no overlapping/invalid keys before saving
      const validation = validateAllOutputKeys();
      if (validation.hasError) {
        setMessage({ type: 'error', text: 'Please fix output keys: duplicates or invalid keys detected.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 6000);
        return;
      }

      // Build response_format from outputs builder + can_review toggle
      let responseFormat = null;
      const formatObj = {};
      // If a row is being edited, auto-accept that draft into a working list before serializing
      let workingOutputs = Array.isArray(formData.response_outputs) ? [...formData.response_outputs] : [];
      if (editingOutputIndex !== null && editingDraftEntry) {
        const draft = {
          key: (editingDraftEntry.key || '').trim(),
          description: editingDraftEntry.description || ''
        };
        workingOutputs[editingOutputIndex] = draft;
      }

      if (Array.isArray(workingOutputs) && workingOutputs.length > 0) {
        const outputObj = {};
        workingOutputs.forEach(entry => {
          if (entry && typeof entry.key === 'string' && entry.key.length > 0) {
            const key = entry.key;
            const desc = typeof entry.description === 'string' ? entry.description.trim() : '';
            outputObj[key] = desc;
          }
        });
        if (Object.keys(outputObj).length > 0) {
          formatObj.output = outputObj;
        }
      }
      if (formData.response_can_review) {
        formatObj.can_review = true;
      }
      if (Object.keys(formatObj).length > 0) {
        responseFormat = formatObj;
      }

      const promptData = {
        id: formData.id.trim(),
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        category: formData.category,
        prompt: formData.prompt || null,
        system_message: formData.system_message || null,
        temperature: parseFloat(formData.temperature) || 1.0,
        model: isWorkflowButtonForm ? null : formData.model,
        modelId: isWorkflowButtonForm ? null : formData.model,
        providerId: isWorkflowButtonForm ? undefined : (formData.provider_id || undefined),
        providerSlug: isWorkflowButtonForm ? undefined : (formData.provider_slug || undefined),
        response_format: responseFormat,
        page_location: formData.page_location || null,
        ingestion_group_slug: selectedGroupSlugs[0] || null,
        ingestion_group_slugs: selectedGroupSlugs,
        tools: Array.isArray(formData.tools) && formData.tools.length > 0 ? formData.tools : null,
        yuml_config: formData.yuml_config || null,
        metadata: {
          api_query_overrides: rowsToOverridesObject(formData.api_query_override_rows || []),
        },
      };

      const runSave = async (payload) => {
        const endpoint = editingPrompt
          ? `${API_BASE_URL}/casc/v1/api/prompts/update/${encodeURIComponent(formData.id.trim())}`
          : `${API_BASE_URL}/casc/v1/api/prompts/create`;
        const action = editingPrompt ? 'updating' : 'creating';
        const headers = await getAuthHeaders();
        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          setMessage({ type: 'success', text: `Prompt ${editingPrompt ? 'updated' : 'created'} successfully` });
          setCollisionModalOpen(false);
          setPendingPromptPayload(null);
          setTimeout(() => {
            setMessage({ type: '', text: '' });
            setShowModal(false);
            setEditingPrompt(null);
            setModalType(null);
            loadPrompts();
          }, 1500);
        } else {
          const errorMsg = data.error || data.message || `Failed to ${action} prompt. Server returned status ${response.status}`;
          setMessage({ type: 'error', text: errorMsg });
          setTimeout(() => setMessage({ type: '', text: '' }), 8000);
        }
      };

      if (selectedGroupSlugs.length >= 2 && String(formData.prompt || '').trim()) {
        try {
          const keysByGroup = {};
          for (const g of selectedGroupSlugs) {
            const r = await fetchUnifiedKeysForGroup(activeSuffix || 'rsmg_sales', g);
            keysByGroup[g] = new Set([...(r.searchOptions || []), ...(r.returnOptions || [])]);
          }
          const collisionResult = detectPromptTemplateCollisions(
            formData.prompt,
            selectedGroupSlugs,
            keysByGroup
          );
          if (collisionResult.hasCollisions) {
            setPendingCollisions({
              collisions: collisionResult.collisions,
              pairs: collisionResult.pairs,
            });
            setPendingPromptPayload(promptData);
            setCollisionModalOpen(true);
            return;
          }
        } catch (collisionErr) {
          console.warn('[PromptsPage] Collision check skipped:', collisionErr?.message || collisionErr);
        }
      }

      await runSave(promptData);
    } catch (error) {
      console.error('[PromptsPage] Exception saving prompt:', error);
      console.error('[PromptsPage] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setMessage({ type: 'error', text: `Network error: ${error.message || 'Failed to connect to server'}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 8000);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPrompt(null);
    setModalType(null);
    setIdTouched(false);
    setCollisionModalOpen(false);
    setPendingPromptPayload(null);
    setFormData({
      id: '',
      name: '',
      description: '',
      prompt: '',
      system_message: '',
      response_format: '',
      temperature: '1',
      model: MODEL_FALLBACK_ID,
      provider_id: '',
      provider_slug: '',
      category: 'general',
      page_location: null,
      ingestion_group_slug: '',
      ingestion_group_slugs: [],
      tools: [],
      response_outputs: [],
      response_can_review: false,
      api_query_override_rows: [],
    });
    setNewOutputKey('');
    setNewOutputDesc('');
    setOutputError('');
    setOutputRowErrors({});
    setModelQuery('');
    setEditingOutputIndex(null);
    setEditingDraftEntry(null);
    setLeftTab('inputs');
    setCopiedToken('');
    outputKeysLoadedRef.current = false;
    rawKeysLoadedRef.current = false;
    setOutputKeys([]);
    setOutputDefinedKeys([]);
    setOutputExistingKeys([]);
    setRawKeys([]);
    setFileKeysFile([]);
    setFileKeysText([]);
    setFileKeysError('');
  };

  // Fetch active suffix when modal opens
  useEffect(() => {
    const fetchActiveSuffix = async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/data-settings/active-suffix`, {
          method: 'POST',
          headers,
          body: JSON.stringify({})
        });
        if (!resp.ok) throw new Error('failed');
        const json = await resp.json().catch(() => ({}));
        const sfx = json?.currentSuffix || json?.suffix || 'rsmg_sales';
        setActiveSuffix(sfx);
      } catch (_) {
        setActiveSuffix('rsmg_sales');
      }
    };
    if (showModal) fetchActiveSuffix();
  }, [showModal]);

  // Load Data Inputs (raw keys) and Output Keys for selected ingestion group(s)
  useEffect(() => {
    const fetchGroupKeysMulti = async (suffix, groupSlugs) => {
      if (!suffix || !groupSlugs.length) return;
      try {
        setRawKeysLoading(true);
        setOutputKeysLoading(true);
        setRawKeysError('');
        setOutputKeysError('');
        const results = await Promise.all(
          groupSlugs.map((g) => fetchUnifiedKeysForGroup(suffix, g))
        );
        const rawLists = results.map((r) => (r.searchOptions || []).map((k) => {
          if (k.startsWith('raw.')) return k.slice(4);
          return null;
        }).filter(Boolean));
        const rawIntersect = intersectFieldLists(rawLists);
        setRawKeys(rawIntersect.sort((a, b) => a.localeCompare(b)));
        rawKeysLoadedRef.current = true;
        const fileLists = results.map((r) => r.fileKeysConvertToText || []);
        setFileKeysFile(intersectFieldLists(fileLists).sort((a, b) => a.localeCompare(b)));
        setFileKeysText([]);
        setFileKeysError('');
        const definedLists = await Promise.all(groupSlugs.map(async (g) => {
          const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(g)}/keys`;
          const keysHeaders = await getAuthHeaders();
          const resp = await fetch(url, { method: 'GET', headers: keysHeaders });
          const json = await resp.json().catch(() => ({}));
          const data = json?.data || {};
          return Array.isArray(data?.output?.defined) ? data.output.defined.map(String) : [];
        }));
        const existingLists = await Promise.all(groupSlugs.map(async (g) => {
          const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(g)}/keys`;
          const keysHeaders = await getAuthHeaders();
          const resp = await fetch(url, { method: 'GET', headers: keysHeaders });
          const json = await resp.json().catch(() => ({}));
          const data = json?.data || {};
          return Array.isArray(data?.output?.existing) ? data.output.existing.map(String) : [];
        }));
        const definedUniq = intersectFieldLists(definedLists).sort((a, b) => a.localeCompare(b));
        const existingUniq = intersectFieldLists(existingLists)
          .filter((k) => k && !definedUniq.includes(k))
          .sort((a, b) => a.localeCompare(b));
        setOutputDefinedKeys(definedUniq);
        setOutputExistingKeys(existingUniq);
        setOutputKeys([...definedUniq, ...existingUniq]);
        outputKeysLoadedRef.current = true;
      } catch (e) {
        console.error('[PromptsPage] Failed to load group keys:', e);
        setRawKeysError('Failed to load raw keys');
        setOutputKeysError('Failed to load output keys');
        setFileKeysError('Failed to load file keys');
        rawKeysLoadedRef.current = false;
        outputKeysLoadedRef.current = false;
      } finally {
        setRawKeysLoading(false);
        setOutputKeysLoading(false);
      }
    };

    if (showModal && selectedGroupSlugs.length > 0) {
      fetchGroupKeysMulti(activeSuffix || 'rsmg_sales', selectedGroupSlugs);
    } else if (showModal && selectedGroupSlugs.length === 0) {
      // If no group selected, clear keys
      setRawKeys([]);
      setOutputDefinedKeys([]);
      setOutputExistingKeys([]);
      setOutputKeys([]);
      setFileKeysFile([]);
      setFileKeysText([]);
      setFileKeysError('');
    }
  }, [showModal, selectedGroupSlugs, activeSuffix]);

  const copyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(''), 1500);
    } catch (e) {
      console.warn('Clipboard copy failed:', e);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePromptNameChange = (value) => {
    setFormData((prev) => {
      const next = { ...prev, name: value };
      if (!editingPrompt && !idTouched) {
        next.id = slugifyName(value);
      }
      return next;
    });
  };

  const handlePromptIdChange = (value) => {
    setIdTouched(true);
    setFormData((prev) => ({ ...prev, id: normalizeSlugInput(value) }));
  };

  const applyModelSelection = (modelRow) => {
    if (!modelRow || !modelRow.id) return;
    setFormData((prev) => ({
      ...prev,
      model: modelRow.id,
      provider_id: modelRow.providerId || prev.provider_id || '',
      provider_slug: modelRow.providerSlug || prev.provider_slug || ''
    }));
    setModelQuery(modelRow.name || modelRow.id);
    setShowModelList(false);
  };

  const handleProviderSelectionChange = (providerKey) => {
    const nextKey = String(providerKey || '').trim();
    if (!nextKey) {
      setFormData((prev) => ({ ...prev, provider_id: '', provider_slug: '' }));
      return;
    }

    const selectedProvider = providers.find((p) => {
      const key = p.id ? `id:${p.id}` : `slug:${p.slug}`;
      return key === nextKey;
    });
    if (!selectedProvider) return;

    const scoped = models.filter((m) => {
      if (selectedProvider.id) {
        return String(m.providerId || '') === String(selectedProvider.id);
      }
      return String(m.providerSlug || '').trim().toLowerCase() === String(selectedProvider.slug || '').trim().toLowerCase();
    });

    setFormData((prev) => {
      const hasCurrentModel = scoped.some((m) => m.id === prev.model);
      const nextModel = hasCurrentModel ? prev.model : (scoped[0]?.id || prev.model || MODEL_FALLBACK_ID);
      return {
        ...prev,
        provider_id: selectedProvider.id || '',
        provider_slug: selectedProvider.slug || '',
        model: nextModel
      };
    });

    const selectedModel = scoped.find((m) => m.id === formData.model) || scoped[0] || null;
    if (selectedModel) {
      setModelQuery(selectedModel.name || selectedModel.id);
    } else {
      setModelQuery('');
    }
  };

  // Validate an output key according to simple JSON-key rules and our non-nesting constraint
  const validateOutputKey = (rawKey, existingKeys = []) => {
    if (typeof rawKey !== 'string') return 'Key must be a string.';
    const key = rawKey;
    if (key.trim() !== key) return 'Key cannot start or end with whitespace.';
    if (!key) return 'Key cannot be empty.';
    // Disallow ASCII control characters (use char codes to avoid control chars in regex)
    for (let i = 0; i < key.length; i++) {
      const code = key.charCodeAt(i);
      if (code >= 0 && code <= 31) {
        return 'Key cannot contain control characters.';
      }
    }
    // Disallow quotes and backslashes to keep keys simple
    if (/["\\]/.test(key)) return 'Key cannot contain quotes or backslashes.';
    // Disallow nesting-like characters
    if (
      key.includes('.') ||
      key.includes('[') ||
      key.includes(']') ||
      key.includes('{') ||
      key.includes('}')
    ) {
      return 'Key cannot contain dots or brackets/braces.';
    }
    // Optional: disallow colon to avoid confusion with JSON
    if (/:/.test(key)) return 'Key cannot contain colons.';
    // Basic duplication check
    if (existingKeys.includes(key)) return 'This key is already added.';
    return null;
  };

  const handleAddOutputKey = () => {
    const candidate = (newOutputKey || '').trim();
    const existing = Array.isArray(formData.response_outputs) ? formData.response_outputs : [];
    const existingKeys = existing.map(e => (typeof e === 'string' ? e : e.key));
    const error = validateOutputKey(candidate, existingKeys);
    if (error) {
      setOutputError(error);
      return;
    }
    const trimmed = candidate.trim();
    setFormData(prev => ({ ...prev, response_outputs: [...existing.map(e => (typeof e === 'string' ? { key: e, description: '' } : e)), { key: trimmed, description: '' }] }));
    setNewOutputKey('');
    setOutputError('');
    setOutputRowErrors({});
    // Do not auto-enter edit; user must click on the text to edit per requirements
    // setEditingOutputIndex(existing.length);
  };

  const handleRemoveOutputKey = (keyToRemove) => {
    // If the row being edited is removed or the same key, exit edit mode
    if (editingOutputIndex !== null) {
      const current = formData.response_outputs[editingOutputIndex];
      const currKey = typeof current === 'string' ? current : current.key;
      if (currKey === keyToRemove) {
        setEditingOutputIndex(null);
        setEditingDraftEntry(null);
      }
    }
    setFormData(prev => ({
      ...prev,
      response_outputs: (prev.response_outputs || []).filter(entry => {
        if (typeof entry === 'string') return entry !== keyToRemove;
        return entry.key !== keyToRemove;
      })
    }));
  };

  // Inline update handlers for output entries
  const handleUpdateOutputEntry = (index, field, value) => {
    // If editing the active row, mutate the draft instead of underlying data
    if (editingOutputIndex === index) {
      setEditingDraftEntry(prev => ({ ...(prev || {}), [field]: value }));
      if (field === 'key') {
        setOutputRowErrors(prevErrs => {
          const errs = { ...prevErrs };
          const others = (formData.response_outputs || [])
            .map((e, i) => (i === index ? null : (typeof e === 'string' ? e : e.key)))
            .filter(Boolean);
          const err = validateOutputKey(value, others);
          if (err) errs[index] = err; else delete errs[index];
          return errs;
        });
      }
      return;
    }

    // For any programmatic updates on non-edit rows (rare), keep previous behavior
    setFormData(prev => {
      const list = Array.isArray(prev.response_outputs) ? [...prev.response_outputs] : [];
      const entry = { ...(typeof list[index] === 'string' ? { key: list[index], description: '' } : list[index]) };
      entry[field] = value;
      list[index] = entry;
      return { ...prev, response_outputs: list };
    });
  };

  const validateAllOutputKeys = () => {
    // Build a working list that includes draft if editing (for validation purposes)
    let list = Array.isArray(formData.response_outputs) ? [...formData.response_outputs] : [];
    if (editingOutputIndex !== null && editingDraftEntry) {
      list[editingOutputIndex] = { key: editingDraftEntry.key || '', description: editingDraftEntry.description || '' };
    }
    const keys = list.map(e => (typeof e === 'string' ? e : (e.key || '')));
    const errs = {};
    const seen = new Set();
    let hasError = false;
    keys.forEach((k, idx) => {
      const otherKeys = keys.filter((_, i) => i !== idx);
      const err = validateOutputKey(k, otherKeys);
      if (err) {
        errs[idx] = err;
        hasError = true;
      }
      const norm = k;
      if (seen.has(norm)) {
        errs[idx] = 'Duplicate key.';
        hasError = true;
      } else if (norm) {
        seen.add(norm);
      }
    });
    setOutputRowErrors(errs);
    return { hasError, errs };
  };

  // Begin editing a row by capturing a draft
  const handleStartEditOutput = (index) => {
    const entry = formData.response_outputs[index];
    const key = typeof entry === 'string' ? entry : entry.key || '';
    const description = typeof entry === 'string' ? '' : (entry.description || '');
    setEditingOutputIndex(index);
    setEditingDraftEntry({ key, description });
    // Pre-validate current key against others
    const others = (formData.response_outputs || [])
      .map((e, i) => (i === index ? null : (typeof e === 'string' ? e : e.key)))
      .filter(Boolean);
    const err = validateOutputKey(key, others);
    setOutputRowErrors(prev => {
      const next = { ...prev };
      if (err) next[index] = err; else delete next[index];
      return next;
    });
  };

  // Confirm edit: apply the draft to the underlying data and exit edit mode
  const handleConfirmEditOutput = () => {
    if (editingOutputIndex === null) return;
    const idx = editingOutputIndex;
    const draft = editingDraftEntry || { key: '', description: '' };
    const trimmedKey = (draft.key || '').trim();
    // Validate before applying
    const others = (formData.response_outputs || [])
      .map((e, i) => (i === idx ? null : (typeof e === 'string' ? e : e.key)))
      .filter(Boolean);
    const err = validateOutputKey(trimmedKey, others);
    if (err) {
      setOutputRowErrors(prev => ({ ...prev, [idx]: err }));
      return;
    }
    setFormData(prev => {
      const list = Array.isArray(prev.response_outputs) ? [...prev.response_outputs] : [];
      list[idx] = { key: trimmedKey, description: (draft.description || '') };
      return { ...prev, response_outputs: list };
    });
    setOutputRowErrors(prev => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
    setEditingOutputIndex(null);
    setEditingDraftEntry(null);
  };

  // Discard edit: do not mutate underlying data, just exit edit mode and clear errors for that row
  const handleDiscardEditOutput = () => {
    if (editingOutputIndex === null) return;
    const idx = editingOutputIndex;
    setEditingOutputIndex(null);
    setEditingDraftEntry(null);
    setOutputRowErrors(prev => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  // Group prompts by page location
  // Sort main prompts so ingestion is first
  // Include ingestion in main prompts even if it doesn't have page_location set
  const mainPrompts = prompts
    .filter(p => p.page_location === 'main' || (p.id === 'ingestion' && !p.page_location))
    .sort((a, b) => {
      if (a.id === 'ingestion') return -1;
      if (b.id === 'ingestion') return 1;
      return 0;
    });
  const reviewPrompts = prompts.filter(p => p.page_location === 'review');
  const managementPrompts = prompts.filter(p => p.page_location === 'management');
  const projectPrompts = prompts.filter(p => p.page_location === 'project_chat_catalog' || p.page_location === 'project');
  // Hide general prompts section - organize into four groups (main with ingestion at top, review, management, project)
  const generalPrompts = [];

  return (
    <>
    <div className="prompts-page">
      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
        </div>
      )}
      <div className="prompt-manager">
        <div className="prompt-header">
          <h1><i className="fas fa-cogs"></i> AI Prompt Manager</h1>
          <p className="prompt-description">
            Manage AI prompts that will appear as buttons on different pages.
            The "Ingestion" prompt is always available and cannot be deleted.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="prompt-actions">
          <button
            className="btn btn-primary"
            onClick={handleAddMainPrompt}
          >
            <i className="fas fa-plus"></i> Add Ingestion Prompt
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddReviewPrompt}
          >
            <i className="fas fa-plus"></i> Add Review Prompt
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddManagementPrompt}
          >
            <i className="fas fa-plus"></i> Add Management Prompt
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddProjectPrompt}
          >
            <i className="fas fa-plus"></i> Add project chat button
          </button>
        </div>

        {loading ? (
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i> Loading prompts...
          </div>
        ) : (
          <div className="prompt-sections">
            {/* Ingestion Page Prompts */}
            {mainPrompts.length > 0 && (
              <div className="prompt-section">
                <h2><i className="fas fa-home"></i> Ingestion Page Prompts</h2>
                <div className="prompt-grid">
                  {mainPrompts.map((prompt) => (
                    <div key={prompt.id} className="prompt-card">
                      <div className="prompt-card-header">
                        <h3 className="prompt-title">{prompt.name}</h3>
                        <span className="prompt-category">{prompt.category}</span>
                      </div>
                      <p className="prompt-description">{prompt.description || 'No description'}</p>
                      <div className="prompt-meta">
                        <span><strong>Model:</strong> {formatStoredPromptModel(prompt.model)}</span>
                        <span><strong>Group:</strong> {(() => {
                          return formatPromptIngestionGroups(prompt, groups);
                        })()}</span>
                      </div>
                      <div className="prompt-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEditPrompt(prompt)}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        {prompt.id !== 'ingestion' && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteClick(prompt.id)}
                          >
                            <i className="fas fa-trash"></i> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Review Page Prompts */}
            {reviewPrompts.length > 0 && (
              <div className="prompt-section">
                <h2><i className="fas fa-check-circle"></i> Review Page Prompts</h2>
                <div className="prompt-grid">
                  {reviewPrompts.map((prompt) => (
                    <div key={prompt.id} className="prompt-card">
                      <div className="prompt-card-header">
                        <h3 className="prompt-title">{prompt.name}</h3>
                        <span className="prompt-category">{prompt.category}</span>
                      </div>
                      <p className="prompt-description">{prompt.description || 'No description'}</p>
                      <div className="prompt-meta">
                        <span><strong>Model:</strong> {formatStoredPromptModel(prompt.model)}</span>
                        <span><strong>Group:</strong> {(() => {
                          return formatPromptIngestionGroups(prompt, groups);
                        })()}</span>
                      </div>
                      <div className="prompt-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEditPrompt(prompt)}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        {prompt.id !== 'ingestion' && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteClick(prompt.id)}
                          >
                            <i className="fas fa-trash"></i> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Management Page Prompts */}
            {managementPrompts.length > 0 && (
              <div className="prompt-section">
                <h2><i className="fas fa-database"></i> Management Page Prompts</h2>
                <div className="prompt-grid">
                  {managementPrompts.map((prompt) => (
                    <div key={prompt.id} className="prompt-card">
                      <div className="prompt-card-header">
                        <h3 className="prompt-title">{prompt.name}</h3>
                        <span className="prompt-category">{prompt.category}</span>
                      </div>
                      <p className="prompt-description">{prompt.description || 'No description'}</p>
                      <div className="prompt-meta">
                        <span><strong>Model:</strong> {formatStoredPromptModel(prompt.model)}</span>
                        <span><strong>Group:</strong> {(() => {
                          return formatPromptIngestionGroups(prompt, groups);
                        })()}</span>
                      </div>
                      <div className="prompt-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEditPrompt(prompt)}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        {prompt.id !== 'ingestion' && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteClick(prompt.id)}
                          >
                            <i className="fas fa-trash"></i> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Project workflow button catalog (assign per project in Edit Project) */}
            {projectPrompts.length > 0 && (
              <div className="prompt-section">
                <h2><i className="fas fa-comments"></i> Project chat button catalog</h2>
                <p className="section-description">Define reusable workflow prompts (with optional ingestion group). Assign which buttons appear in each project from the project edit dialog; linked buttons show next to Attach in that project&apos;s chats.</p>
                <div className="prompt-grid">
                  {projectPrompts.map((prompt) => (
                    <div key={prompt.id} className="prompt-card">
                      <div className="prompt-card-header">
                        <h3 className="prompt-title">{prompt.name}</h3>
                        <span className="prompt-category">{prompt.category}</span>
                      </div>
                      <p className="prompt-description">{prompt.description || 'No description'}</p>
                      <div className="prompt-meta">
                        <span><strong>Model:</strong> {formatStoredPromptModel(prompt.model)}</span>
                        <span><strong>Group:</strong> {(() => {
                          return formatPromptIngestionGroups(prompt, groups);
                        })()}</span>
                      </div>
                      <div className="prompt-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEditPrompt(prompt)}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteClick(prompt.id)}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* General Prompts */}
            {generalPrompts.length > 0 && (
              <div className="prompt-section">
                <h2><i className="fas fa-list"></i> General Prompts</h2>
                <div className="prompt-grid">
                  {generalPrompts.map((prompt) => (
                    <div key={prompt.id} className="prompt-card">
                      <div className="prompt-card-header">
                        <h3 className="prompt-title">{prompt.name}</h3>
                        <span className="prompt-category">{prompt.category}</span>
                      </div>
                      <p className="prompt-description">{prompt.description || 'No description'}</p>
                      <div className="prompt-meta">
                        <span><strong>Model:</strong> {formatStoredPromptModel(prompt.model)}</span>
                      </div>
                      <div className="prompt-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEditPrompt(prompt)}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        {prompt.id !== 'ingestion' && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteClick(prompt.id)}
                          >
                            <i className="fas fa-trash"></i> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {prompts.length === 0 && (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No prompts configured yet. Use the buttons above to add prompts.</p>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal show">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Confirm Delete</h2>
                <span className="close" onClick={handleDeleteCancel}>&times;</span>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this prompt?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDeleteCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                >
                  <i className="fas fa-trash"></i> Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Prompt Editor Modal */}
        {showModal && (
          <div className="modal show">
            <div className="modal-content large prompt-editor-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">
                  {editingPrompt ? 'Edit Prompt' : (modalType === 'main' ? 'New Ingestion Prompt' : `New ${modalType ? modalType.charAt(0).toUpperCase() + modalType.slice(1) : ''} Prompt`)}
                </h2>
                <span className="close" onClick={handleCloseModal}>&times;</span>
              </div>
              <div className="prompt-editor-identity" role="region" aria-label="Prompt identity">
                <NameSlugFields
                  nameClassName="identity-field identity-field--name"
                  slugClassName="identity-field identity-field--id"
                  name={formData.name}
                  slug={formData.id}
                  onNameChange={handlePromptNameChange}
                  onSlugChange={handlePromptIdChange}
                  slugConflict={idConflict}
                  isEdit={!!editingPrompt}
                  nameLabel="Name"
                  slugLabel="Identifier"
                  nameId="promptName"
                  slugId="promptId"
                  namePlaceholder="Display name"
                  slugPlaceholder="e.g., document-agent-system"
                  slugHelp="Identifier cannot be changed after creation"
                />
                <div className="identity-field identity-field--model">
                  <label>Model</label>
                  <div className="prompt-model-readonly" title={promptEditorModelDisplay}>
                    <i className="fas fa-microchip" aria-hidden="true" />
                    <span>{promptEditorModelDisplay}</span>
                  </div>
                </div>
                <div className="identity-field identity-field--desc">
                  <label htmlFor="promptDescription">Description</label>
                  <textarea
                    id="promptDescription"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="What this prompt does"
                    rows="2"
                  />
                </div>
              </div>
              <div className="modal-body two-col">
                {/* Left Sidebar */}
                <aside className="prompt-leftbar">
                  <div className="leftbar-tabs">
                    <button
                      type="button"
                      className={`leftbar-tab ${leftTab === 'inputs' ? 'active' : ''}`}
                      onClick={() => setLeftTab('inputs')}
                    >
                      <i className="fas fa-plug"></i> Data Inputs
                    </button>
                    <button
                      type="button"
                      className={`leftbar-tab ${leftTab === 'help' ? 'active' : ''}`}
                      onClick={() => setLeftTab('help')}
                    >
                      <i className="fas fa-question-circle"></i> Help
                    </button>
                  </div>

                  {leftTab === 'inputs' ? (
                    <div className="leftbar-content">
                      <h4>Raw Keys</h4>
                      {rawKeysLoading ? (
                        <div className="loading small"><i className="fas fa-spinner fa-spin"></i> Loading...</div>
                      ) : rawKeysError ? (
                        <div className="alert alert-error small"><i className="fas fa-exclamation-circle"></i> {rawKeysError}</div>
                      ) : rawKeys.length === 0 ? (
                        <div className="empty small">No raw keys found. Configure Data Input Settings.</div>
                      ) : (
                        <ul className="token-list">
                          {rawKeys.map(k => (
                            <li key={k}>
                              <button type="button" className="token" onClick={() => copyToken(`{ raw.${k} }`)} title="Click to copy">
                                <span className="code">{`{ raw.${k} }`}</span>
                                {copiedToken === `{ raw.${k} }` && <i className="fas fa-check copied"></i>}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      <h4 style={{ marginTop: '16px' }}>Files</h4>
                      {rawKeysLoading ? (
                        <div className="loading small"><i className="fas fa-spinner fa-spin"></i> Loading...</div>
                      ) : fileKeysError ? (
                        <div className="alert alert-error small"><i className="fas fa-exclamation-circle"></i> {fileKeysError}</div>
                      ) : (
                        <>
                          <div className="muted tiny" style={{ marginBottom: 6 }}>
                            Use <code>{'{ file.<field> }'}</code> in prompts. File-type attaches; Text-type inlines.
                          </div>
                          <div className="small-header">Attachments (file)</div>
                          <ul className="token-list">
                            {fileKeysFile.length === 0 && (
                              <li className="empty small">No file attachments detected.</li>
                            )}
                            {fileKeysFile.map(k => (
                              <li key={k}>
                                <button
                                  type="button"
                                  className="token"
                                  onClick={() => copyToken(`{ ${k} }`)}
                                  title="Attaches file; click to copy"
                                >
                                  <span className="code">{`{ ${k} }`}</span>
                                  <span className="badge" style={{ marginLeft: 6 }}>file</span>
                                  {copiedToken === `{ ${k} }` && <i className="fas fa-check copied"></i>}
                                </button>
                              </li>
                            ))}
                          </ul>
                          <div className="small-header" style={{ marginTop: 10 }}>Inline text</div>
                          <ul className="token-list">
                            {fileKeysText.length === 0 && (
                              <li className="empty small">No inline text files detected.</li>
                            )}
                            {fileKeysText.map(k => (
                              <li key={k}>
                                <button
                                  type="button"
                                  className="token"
                                  onClick={() => copyToken(`{ ${k} }`)}
                                  title="Inlines text; click to copy"
                                >
                                  <span className="code">{`{ ${k} }`}</span>
                                  <span className="badge" style={{ marginLeft: 6 }}>text</span>
                                  {copiedToken === `{ ${k} }` && <i className="fas fa-check copied"></i>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}

                      <h4 style={{ marginTop: '16px' }}>Defined Output Keys</h4>
                      {outputKeysLoading ? (
                        <div className="loading small"><i className="fas fa-spinner fa-spin"></i> Loading...</div>
                      ) : outputKeysError ? (
                        <div className="alert alert-error small"><i className="fas fa-exclamation-circle"></i> {outputKeysError}</div>
                      ) : (
                        <ul className="token-list">
                          {(outputDefinedKeys.length === 0 ? [] : outputDefinedKeys).map(k => (
                            <li key={k}>
                              <button type="button" className="token" onClick={() => copyToken(`{ ${k} }`)} title="Click to copy">
                                <span className="code">{`{ ${k} }`}</span>
                                {copiedToken === `{ ${k} }` && <i className="fas fa-check copied"></i>}
                              </button>
                            </li>
                          ))}
                          {outputDefinedKeys.length === 0 && <li className="empty small">No defined keys.</li>}
                        </ul>
                      )}
                      <h4 style={{ marginTop: '16px' }}>Existing Output Keys</h4>
                      {outputKeysLoading ? (
                        <div className="loading small"><i className="fas fa-spinner fa-spin"></i> Loading...</div>
                      ) : outputKeysError ? (
                        <div className="alert alert-error small"><i className="fas fa-exclamation-circle"></i> {outputKeysError}</div>
                      ) : (
                        <ul className="token-list">
                          {(outputExistingKeys.length === 0 ? [] : outputExistingKeys).map(k => (
                            <li key={k}>
                              <button type="button" className="token" onClick={() => copyToken(`{ ${k} }`)} title="Click to copy">
                                <span className="code">{`{ ${k} }`}</span>
                                {copiedToken === `{ ${k} }` && <i className="fas fa-check copied"></i>}
                              </button>
                            </li>
                          ))}
                          {outputExistingKeys.length === 0 && <li className="empty small">No existing keys.</li>}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <div className="leftbar-content help">
                      <h4>Prompt templating</h4>
                      <p className="muted small">Applies to User Message and each Output description. Does not apply to System Message.</p>
                      <ul className="help-list">
                        <li>Use single braces to inject values: <code>{'{ raw.field }'}</code>, <code>{'{ some_output_key }'}</code>.</li>
                        <li>Whitespace inside braces is ignored: <code>{'{ raw.doc_name }'}</code> equals <code>{'{raw.doc_name}'}</code></li>
                        <li>Escape literal braces by doubling: <code>{'{{'}</code> becomes <code>{'{'}</code> and <code>{'}}'}</code> becomes <code>{'}'}</code></li>
                        <li>Missing/null paths render as empty string. Objects/arrays are JSON-stringified.</li>
                        <li>Files: reference discovered file fields with <code>{'{ file.&lt;field&gt; }'}</code>. File-type values attach the filename; Text-type values inline the content.</li>
                      </ul>
                      <div className="example">
                        <div className="example-title">Example</div>
                        <div className="code-block">
                          <div>Input: <code>{'{ "raw": { "doc_name": "document54.pdf" }, "review": "Boring" }'}</code></div>
                          <div>Prompt: <code>{'Please look at { raw.doc_name } and determine if { review } is accurate.'}</code></div>
                          <div>Result: <code>{'Please look at document54.pdf and determine if Boring is accurate.'}</code></div>
                        </div>
                      </div>
                    </div>
                  )}
                </aside>

                {/* Configurable fields — two columns under identity strip */}
                <form id="promptForm" className="prompt-form">
                  <div className="prompt-config-columns">
                  <div className="prompt-config-col">
                  <div className="form-group">
                    <label>
                      Ingestion groups{!isProjectCatalogForm && <span className="required">*</span>}
                    </label>
                    <IngestionGroupMultiPicker
                      suffix={activeSuffix || 'rsmg_sales'}
                      selectedSlugs={selectedGroupSlugs}
                      onChange={(slugs) => {
                        handleInputChange('ingestion_group_slugs', slugs);
                        handleInputChange('ingestion_group_slug', slugs[0] || '');
                      }}
                      disabled={false}
                    />
                    <small className="form-help">
                      {isProjectCatalogForm
                        ? 'Optional for project chat catalog buttons. Add up to 5 groups when this prompt should scope batch runs.'
                        : `Add one or more groups (up to ${MAX_TOOL_INGESTION_GROUPS}). Batch runs accept entries from any selected group.`}
                    </small>
                    {selectedGroupSlugs.length > 1 && (
                      <small className="form-help" style={{ display: 'block' }}>
                        Input tokens and placeholders only list fields shared by every selected group.
                      </small>
                    )}
                  </div>
                  {isWorkflowButtonForm && selectedGroupSlugs.length > 0 && (
                    <div className="form-group">
                      <label>API query overrides (optional)</label>
                      <ApiQueryParamsEditor
                        rows={formData.api_query_override_rows?.length
                          ? formData.api_query_override_rows
                          : [{ key: '', value: '', label: '' }]}
                        onChange={(rows) => handleInputChange('api_query_override_rows', rows)}
                        endpoint=""
                        hint="Override API ingestion query parameters when this workflow runs ingest for its linked group(s). Leave blank to use group defaults from Settings → Ingestion."
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="promptText" className="label-with-info">
                      <span>User Message</span>
                      <i
                        className="fas fa-info-circle info-icon"
                        title="Templating supported. Use { path } to inject values from the Input JSON (e.g., { raw.doc_name }, { review }). Whitespace inside braces is ignored. Escape literal braces with double braces: {{ and }}. If a path is missing/null, it renders as an empty string; objects/arrays are JSON-stringified."
                        aria-label="User Message templating info"
                      ></i>
                    </label>
                    <textarea
                      id="promptText"
                      className="large"
                      value={formData.prompt}
                      onChange={(e) => handleInputChange('prompt', e.target.value)}
                      placeholder="Enter your prompt user message here..."
                      rows="6"
                    ></textarea>
                    {(() => {
                      const det = detectPlaceholders(formData.prompt);
                      const hasVars = det.variables && det.variables.length > 0;
                      const hasErrs = det.invalids && det.invalids.length > 0;
                      if (!hasVars && !hasErrs) return null;
                      return (
                        <div className="placeholder-hints">
                          {hasVars && (
                            <div className="vars">
                              <span className="label">Detected variables:</span>
                              <div className="var-badges">
                                {det.variables.map(v => (
                                  <span key={v} className="var-badge">{v}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {hasErrs && (
                            <div className="placeholder-errors">
                              {det.invalids.map((inv, i) => (
                                <div key={i} className="error-item">
                                  <i className="fas fa-exclamation-triangle"></i>
                                  <span>{inv.raw} — {inv.reason}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="form-group">
                    <label htmlFor="systemMessage" className="label-with-info">
                      <span>System Message</span>
                      <i
                        className="fas fa-info-circle info-icon"
                        title="Templating does not apply here. The system message is sent exactly as entered."
                        aria-label="System Message info"
                      ></i>
                    </label>
                    <textarea
                      id="systemMessage"
                      value={formData.system_message}
                      onChange={(e) => handleInputChange('system_message', e.target.value)}
                      placeholder="Optional system message for the AI"
                      rows="4"
                    ></textarea>
                  </div>
                  <div className="form-group prompt-tools-panel">
                    <div className="prompt-tools-panel-header">
                      <label className="label-with-info" style={{ margin: 0 }}>
                        <span>Tools</span>
                        <i
                          className="fas fa-info-circle info-icon"
                          title="Optional allowlist. When set, only these tools can be used when this prompt runs. Leave empty to allow all tools in the project global allowlist (horizon tools plus active web search)."
                          aria-label="Tools info"
                        ></i>
                      </label>
                    </div>
                    <ToolSelector
                      availableTools={availableTools}
                      selectedIds={formData.tools || []}
                      onChange={(ids) => handleInputChange('tools', ids)}
                      loading={toolsLoading}
                      placeholder="Search tools by name or slug, press Enter to add"
                    />
                    <small className="prompt-tools-hint">
                      Add one or more tools to limit scope. Empty means the full project global allowlist (all horizon tools plus active web search).
                    </small>
                  </div>
                  </div>
                  <div className="prompt-config-col">
                  {!isWorkflowButtonForm && showPdfCapabilityWarning && (
                    <div className="alert alert-warning" style={{ marginTop: 0, marginBottom: 12 }}>
                      <i className="fas fa-triangle-exclamation"></i> This horizon has file placeholders, but the selected model does not advertise PDF/file support.
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="outputsBuilder" className="label-with-info">
                      <span>Outputs</span>
                      <i
                        className="fas fa-info-circle info-icon"
                        title="Add output keys (non-nested) with optional descriptions. Descriptions support templating like the User Message: use { path } to inject values from Input JSON (e.g., { raw.doc_name }). Escape literal braces with {{ and }}."
                        aria-label="Outputs info"
                      ></i>
                    </label>
                    <div id="outputsBuilder">
                      <div className="outputs-add-row">
                        <input
                          type="text"
                          placeholder="Add an output key (e.g., color, length)"
                          value={newOutputKey}
                          onChange={(e) => {
                            setNewOutputKey(e.target.value);
                            if (outputError) setOutputError('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); handleAddOutputKey(); }
                          }}
                        />
                        <button type="button" className="btn btn-secondary" onClick={() => handleAddOutputKey()} disabled={!newOutputKey.trim()}>
                          Add
                        </button>
                      </div>
                      {outputError && (
                        <div className="alert alert-error" style={{ marginBottom: '8px' }}>
                          <i className="fas fa-exclamation-circle"></i> {outputError}
                        </div>
                      )}
                      {Array.isArray(formData.response_outputs) && formData.response_outputs.length > 0 ? (
                        <div className="outputs-list">
                          {formData.response_outputs.map((entry, idx) => {
                            const key = typeof entry === 'string' ? entry : entry.key;
                            const desc = typeof entry === 'string' ? '' : (entry.description || '');
                            const rowError = outputRowErrors[idx];
                            const isEditing = editingOutputIndex === idx;
                            return (
                              <div key={idx} className={`outputs-row ${isEditing ? 'edit' : 'view'} ${rowError ? 'has-error' : ''}`}>
                                {isEditing ? (
                                  <>
                                    <input
                                      className="key-input"
                                      type="text"
                                      value={(editingDraftEntry && isEditing ? editingDraftEntry.key : key) || ''}
                                      placeholder="key"
                                      onChange={(e) => handleUpdateOutputEntry(idx, 'key', e.target.value)}
                                      onBlur={(e) => {
                                        // Trim on blur and revalidate (only draft)
                                        const current = (editingDraftEntry && isEditing ? editingDraftEntry.key : key) || '';
                                        handleUpdateOutputEntry(idx, 'key', current.trim());
                                      }}
                                    />
                                    <textarea
                                      className="desc-input"
                                      rows={2}
                                      value={(editingDraftEntry && isEditing ? editingDraftEntry.description : desc) || ''}
                                      placeholder="Description (what to put in this field)"
                                      onChange={(e) => handleUpdateOutputEntry(idx, 'description', e.target.value)}
                                    />
                                    {(() => {
                                      const currDesc = (editingDraftEntry && isEditing ? editingDraftEntry.description : desc) || '';
                                      const det = detectPlaceholders(currDesc);
                                      const hasVars = det.variables && det.variables.length > 0;
                                      const hasErrs = det.invalids && det.invalids.length > 0;
                                      if (!hasVars && !hasErrs) return null;
                                      return (
                                        <div className="placeholder-hints compact">
                                          {hasVars && (
                                            <div className="vars">
                                              <span className="label">Variables:</span>
                                              <div className="var-badges">
                                                {det.variables.map(v => (
                                                  <span key={v} className="var-badge">{v}</span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {hasErrs && (
                                            <div className="placeholder-errors">
                                              {det.invalids.map((inv, i) => (
                                                <div key={i} className="error-item">
                                                  <i className="fas fa-exclamation-triangle"></i>
                                                  <span>{inv.raw} — {inv.reason}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    <div className="row-actions">
                                      <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={handleConfirmEditOutput}
                                        disabled={!!rowError}
                                        title="Confirm changes"
                                      >
                                        <i className="fas fa-check"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={handleDiscardEditOutput}
                                        title="Discard changes"
                                      >
                                        <i className="fas fa-undo"></i>
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div
                                      className="output-key-display"
                                      title={key}
                                      onClick={() => handleStartEditOutput(idx)}
                                    >
                                      {key || 'Unnamed key'}
                                    </div>
                                    <button
                                      type="button"
                                      className="tag-remove-btn"
                                      aria-label={`Remove ${key || 'output'}`}
                                      onClick={() => handleRemoveOutputKey(key)}
                                      title="Remove"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                    <div
                                      className="output-desc-display"
                                      title={desc}
                                      onClick={() => handleStartEditOutput(idx)}
                                    >
                                      {desc || <span className="muted">Click to add a description...</span>}
                                    </div>
                                  </>
                                )}
                                {rowError && (
                                  <div className="row-error"><i className="fas fa-exclamation-triangle"></i> {rowError}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <small>No outputs added. Leave empty for free-form responses.</small>
                      )}
                    </div>
                  </div>
                  {/* Can Review Toggle */}
                  <div className="form-group">
                    <label htmlFor="canReviewToggle" className="label-with-info">
                      <span>Can Review</span>
                      <i
                        className="fas fa-info-circle info-icon"
                        title="When enabled, the agent can approve or disapprove entries during review."
                        aria-label="Can Review info"
                      ></i>
                    </label>
                    <div className="toggle small">
                      <input
                        id="canReviewToggle"
                        type="checkbox"
                        checked={!!formData.response_can_review}
                        onChange={(e) => handleInputChange('response_can_review', e.target.checked)}
                      />
                      <span className="toggle-track" aria-hidden="true">
                        <span className="toggle-thumb"></span>
                      </span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>YUML output</label>
                    <YumlConfigFields
                      suffix={activeSuffix || 'rsmg_sales'}
                      value={formData.yuml_config}
                      onChange={(yuml_config) => setFormData((prev) => ({ ...prev, yuml_config }))}
                      summaryKeyOptions={(formData.response_outputs || []).map((o) => o.key).filter(Boolean)}
                      emitOn="after_output"
                    />
                  </div>
                  {!isWorkflowButtonForm && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="provider">Provider</label>
                      <select
                        id="provider"
                        value={(() => {
                          if (formData.provider_id) return `id:${formData.provider_id}`;
                          if (formData.provider_slug) return `slug:${formData.provider_slug}`;
                          return '';
                        })()}
                        onChange={(e) => handleProviderSelectionChange(e.target.value)}
                      >
                        <option value="">Auto from model</option>
                        {providers.map((provider) => {
                          const key = provider.id ? `id:${provider.id}` : `slug:${provider.slug}`;
                          return (
                            <option key={key} value={key}>
                              {provider.name}{provider.apiType ? ` (${provider.apiType})` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="form-group model-combobox">
                      <label htmlFor="model">Model: <span className="required">*</span></label>
                      <input
                        id="model"
                        type="text"
                        ref={modelInputRef}
                        value={modelQuery}
                        onChange={(e) => { setModelQuery(e.target.value); setShowModelList(true); setModelHighlight(0); }}
                        onFocus={() => setShowModelList(true)}
                        onKeyDown={(e) => {
                          if (!showModelList) return;
                          if (e.key === 'ArrowDown') { e.preventDefault(); setModelHighlight(h => Math.min(h + 1, filteredModels.length - 1)); }
                          if (e.key === 'ArrowUp') { e.preventDefault(); setModelHighlight(h => Math.max(h - 1, 0)); }
                          if (e.key === 'Enter') { e.preventDefault(); const m = filteredModels[modelHighlight]; if (m) { applyModelSelection(m); } }
                          if (e.key === 'Escape') { setShowModelList(false); }
                        }}
                        placeholder={(() => {
                          const curr = providerScopedModels.find(m => m.id === formData.model) || models.find(m => m.id === formData.model);
                          return curr ? `${curr.name || curr.id}` : formData.model || 'Select a model';
                        })()}
                        autoComplete="off"
                      />
                      {showModelList && (
                        <div className="model-dropdown" ref={modelListRef}>
                          {filteredModels.length === 0 ? (
                            <div className="model-item disabled">No models found</div>
                          ) : (
                            filteredModels.map((m, idx) => (
                              <div
                                key={m.id}
                                className={`model-item ${idx === modelHighlight ? 'active' : ''}`}
                                onMouseEnter={() => setModelHighlight(idx)}
                                onMouseDown={(e) => { e.preventDefault(); applyModelSelection(m); }}
                              >
<span className="model-name-wrap">
                                  <span className="model-name">{m.providerName || m.provider ? `${m.providerName || m.provider}: ${m.name || m.id}` : (m.name || m.id)}</span>
                                  {m.name && m.id && m.name !== m.id && (
                                    <span className="model-id">{m.id}</span>
                                  )}
                                </span>
                                <ModelCapabilityIcons model={m} />
                              </div>
                            ))
                          )}
                        </div>
                      )}
                      <div className="model-capabilities-row">
                        <small>Capabilities:</small>
                        <ModelCapabilityIcons model={selectedModel || { id: formData.model, capabilities: { acceptsText: true } }} hideDisabled={false} />
                      </div>
                    </div>
                  </div>
                  )}
                  </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSavePrompt} disabled={!!idConflict}>
                  {editingPrompt ? 'Update' : 'Create'} Prompt
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    <ToolCollisionConfirmModal
      isOpen={collisionModalOpen}
      collisions={pendingCollisions.collisions}
      pairs={pendingCollisions.pairs}
      onSave={async () => {
        if (pendingPromptPayload) {
          const headers = await getAuthHeaders();
          const endpoint = editingPrompt
            ? `${API_BASE_URL}/casc/v1/api/prompts/update/${encodeURIComponent(String(pendingPromptPayload.id || '').trim())}`
            : `${API_BASE_URL}/casc/v1/api/prompts/create`;
          const response = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(pendingPromptPayload),
          });
          const data = await response.json();
          if (response.ok && data.success) {
            setMessage({ type: 'success', text: `Prompt ${editingPrompt ? 'updated' : 'created'} successfully` });
            setCollisionModalOpen(false);
            setPendingPromptPayload(null);
            setTimeout(() => {
              setMessage({ type: '', text: '' });
              setShowModal(false);
              setEditingPrompt(null);
              setModalType(null);
              loadPrompts();
            }, 1500);
          } else {
            setMessage({ type: 'error', text: data.error || data.message || 'Failed to save prompt' });
          }
        }
      }}
      onFix={() => {
        setCollisionModalOpen(false);
        setPendingPromptPayload(null);
      }}
    />
    </>
  );
}

export default PromptsPage;
